import React from "react";
import {
Box,
Image,
Text,
Badge,
Button,
HStack,
VStack,
Tooltip,
IconButton,
useDisclosure,
} from "@chakra-ui/react";
import { InfoIcon, RepeatIcon, CheckCircleIcon } from "@chakra-ui/icons";
import { useCart } from "../context/CartContext";
import { useToast } from "@chakra-ui/react";
import MealInfoModal from "./MealInfoModal";

export default function MealCard({ meal, onReplace, canReplace = true }) {
const {
name,
image,
tags = [],
dietTags = [],
genomeCautions = [],
biomarkerCautions = [],
costToMake,
} = meal;

const { isOpen, onOpen, onClose } = useDisclosure();
const { addToCart, cartItems } = useCart();
const toast = useToast();

// ✅ Price logic
const price =
typeof costToMake === "number" && costToMake > 0
? Math.round(costToMake / 2)
: 1500;

const priceLabel = `₦${price.toLocaleString()}`;

// ✅ Check if already in cart
const inCart = cartItems?.some((item) => item._id === meal._id);

// ✅ Add price to meal before adding to cart
const handleBuy = () => {
if (!inCart) {
const mealWithPrice = { ...meal, price }; // ensure price is included
addToCart(mealWithPrice);

toast({
title: "Added to cart",
description: `${name} has been added to your cart.`,
status: "success",
duration: 2000,
isClosable: true,
});
}
};

// ✅ Display top 2 tags, prioritize warnings
const warningTags = [...genomeCautions, ...biomarkerCautions];
const displayTags = [
...warningTags.map((tag) => ({ tag, color: "red" })),
...dietTags.map((tag) => ({ tag, color: "purple" })),
...tags.map((tag) => ({ tag, color: "blue" })),
].slice(0, 2);

return (
  <Box
    borderWidth="1px"
    borderRadius="2xl"
    overflow="hidden"
    boxShadow="lg"
    bgGradient="linear(to-br, white, green.50 90%)"
    transition="all 0.2s"
    _hover={{ boxShadow: "2xl", transform: "translateY(-4px) scale(1.015)" }}
    maxW="340px"
    mx="auto"
    position="relative"
    p={0}
  >
    <VStack spacing={3} align="start">
      <Image
        src={image || "/placeholder.jpg"}
        alt={name}
        borderRadius="xl"
        objectFit="cover"
        w="100%"
        h="170px"
        boxShadow="md"
        bg="gray.100"
      />
      <Box w="100%" px={4}>
        <Text fontWeight="bold" fontSize="xl" noOfLines={2} color="gray.800">
          {name}
        </Text>
        <Text fontSize="md" color="green.700" fontWeight="bold" mt={1}>
          {priceLabel}
          <Text as="span" color="gray.500" fontSize="sm">
            /portion
          </Text>
        </Text>
      </Box>

      <HStack spacing={2} flexWrap="wrap" mt={1} mb={1} px={4}>
        {displayTags.map(({ tag, color }) => (
          <Badge
            key={tag}
            colorScheme={color}
            borderRadius="md"
            px={2}
            fontSize="xs"
            textTransform="capitalize"
            bg={`${color}.100`}
            color={`${color}.700`}
            fontWeight="semibold"
            boxShadow="sm"
          >
            {tag}
          </Badge>
        ))}
      </HStack>

      <HStack justify="space-between" w="100%" pt={2} px={4} pb={4}>
        <Button
          colorScheme="green"
          size="md"
          flex={1}
          leftIcon={inCart ? <CheckCircleIcon /> : null}
          onClick={handleBuy}
          isDisabled={inCart}
          borderRadius="xl"
          fontWeight="bold"
          boxShadow="md"
          bgGradient={inCart ? "linear(to-r, green.400, green.600)" : "linear(to-r, green.400, green.500)"}
          color="white"
          _hover={{ bg: "green.600", boxShadow: "xl" }}
          _active={{ bg: "green.700" }}
        >
          {inCart ? "In Cart" : "Buy"}
        </Button>

        <Tooltip label="Replace meal" hasArrow>
          <Box as="span">
            <IconButton
              icon={<RepeatIcon />}
              onClick={() => onReplace?.(meal._id, meal)}
              isDisabled={!canReplace}
              colorScheme={canReplace ? "orange" : "gray"}
              variant="ghost"
              size="md"
              aria-label="Replace"
              borderRadius="full"
              boxShadow="sm"
            />
          </Box>
        </Tooltip>

        <Tooltip label="More info" hasArrow>
          <Box as="span">
            <IconButton
              icon={<InfoIcon />}
              onClick={onOpen}
              colorScheme="gray"
              variant="ghost"
              size="md"
              aria-label="Info"
              borderRadius="full"
              boxShadow="sm"
            />
          </Box>
        </Tooltip>
      </HStack>
    </VStack>

    {/* Modal */}
    <MealInfoModal meal={meal} isOpen={isOpen} onClose={onClose} />
  </Box>
);
}





























